import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UsersThree, Copy, ArrowsClockwise, SignOut } from "@phosphor-icons/react";
import { createTeamApi, joinTeamApi, leaveTeamApi, regenTeamCodeApi, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const input = "h-10 w-full rounded-xl border border-black/10 bg-white px-3 text-sm text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

function NoTeam({ onChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const canCreate = (user.plan === "team" && user.plan_status === "active") || user.role === "admin";
  const create = async () => { try { onChange(await createTeamApi(name)); toast.success("Team created — share the invite code"); } catch (e) { err(e); } };
  const join = async () => { try { onChange(await joinTeamApi(code)); toast.success("You joined the team"); } catch (e) { err(e); } };
  return (
    <div className="space-y-4" data-testid="team-empty">
      <div className="rounded-2xl bg-[#FAFAFC] p-4">
        <p className="text-sm font-semibold text-ink">Join a team</p>
        <p className="mt-0.5 text-xs text-mist">Ask your teammate for their 8-letter invite code.</p>
        <div className="mt-3 flex gap-2">
          <input data-testid="team-join-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. K7Q2M9XP" className={`${input} font-mono tracking-widest`} />
          <button data-testid="team-join-button" onClick={join} disabled={code.length < 6} className="h-10 shrink-0 rounded-full bg-ink px-4 text-sm font-semibold text-white disabled:opacity-40">Join</button>
        </div>
      </div>
      <div className="rounded-2xl bg-[#FAFAFC] p-4">
        <p className="text-sm font-semibold text-ink">Create a team</p>
        {canCreate ? (
          <div className="mt-3 flex gap-2">
            <input data-testid="team-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Team name" className={input} />
            <button data-testid="team-create-button" onClick={create} className="h-10 shrink-0 rounded-full bg-brand px-4 text-sm font-semibold text-white">Create</button>
          </div>
        ) : (
          <p className="mt-1 text-xs text-mist">
            Creating a team is part of the Team plan.{" "}
            <button data-testid="team-upgrade-link" onClick={() => navigate("/pricing")} className="font-medium text-brand hover:underline">See Team plan</button>
          </p>
        )}
      </div>
    </div>
  );
}

export function TeamPanel({ team, onChange }) {
  if (!team) return <NoTeam onChange={onChange} />;
  const copy = async () => { await navigator.clipboard.writeText(team.invite_code); toast.success("Invite code copied"); };
  const regen = async () => { try { onChange(await regenTeamCodeApi()); toast.success("New invite code created"); } catch (e) { err(e); } };
  const leave = async () => {
    if (!window.confirm(team.is_owner ? "Delete this team for everyone?" : "Leave this team?")) return;
    try { await leaveTeamApi(); onChange(null); toast.success(team.is_owner ? "Team deleted" : "You left the team"); } catch (e) { err(e); }
  };
  return (
    <div className="rounded-2xl bg-[#FAFAFC] p-4" data-testid="team-panel">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink"><UsersThree size={17} weight="fill" className="text-brand" /> <span data-testid="team-name">{team.name}</span></p>
        <button data-testid="team-leave-button" onClick={leave} className="flex items-center gap-1 text-xs text-mist hover:text-[#D70015]"><SignOut size={13} /> {team.is_owner ? "Delete" : "Leave"}</button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <span data-testid="team-invite-code" className="flex-1 rounded-xl border border-dashed border-black/15 bg-white px-3 py-2 text-center font-mono text-base font-semibold tracking-[0.3em] text-ink">{team.invite_code}</span>
        <button data-testid="team-copy-code-button" onClick={copy} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/5 hover:bg-black/10" title="Copy code"><Copy size={15} /></button>
        {team.is_owner && <button data-testid="team-regen-code-button" onClick={regen} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/5 hover:bg-black/10" title="New code"><ArrowsClockwise size={15} /></button>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="team-members">
        {team.members.map((m) => (
          <span key={m.user_id} className="rounded-full bg-white px-2.5 py-1 text-[11px] text-ink shadow-sm">{m.name || m.email}</span>
        ))}
      </div>
    </div>
  );
}
